import React, { useState } from 'react';
import { Button, Modal, message } from 'antd';
import { PaperClipOutlined, EyeOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { followAPI } from '../services/follow-api';
import { indexedDBStorage } from '../services/storage/indexedDB';

export type FollowAttachFile = {
  attachfileno: number;
  name: string;
  size: number;
};

interface FollowAttachmentModalProps {
  open: boolean;
  files: FollowAttachFile[];
  onClose: () => void;
  onFilesChange: (newFiles: FollowAttachFile[]) => void;
  onFileDeleted: (attachfileno: number) => void;
  onFilePreview: (file: FollowAttachFile) => void;
  onFileDownload: (file: FollowAttachFile) => void;
  /** ファイル一覧モーダルの zIndex */
  zIndex?: number;
  /** 削除確認モーダルの zIndex */
  confirmZIndex?: number;
  /** ファイル一覧を maxHeight でスクロール可能にする */
  scrollable?: boolean;
}

const FollowAttachmentModal: React.FC<FollowAttachmentModalProps> = ({
  open,
  files,
  onClose,
  onFilesChange,
  onFileDeleted,
  onFilePreview,
  onFileDownload,
  zIndex,
  confirmZIndex,
  scrollable = false,
}) => {
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);

  const targetFile = deleteTargetIndex !== null ? files[deleteTargetIndex] : null;

  const handleDeleteConfirm = async () => {
    if (deleteTargetIndex === null || !targetFile) return;
    if (targetFile.attachfileno) {
      try {
        const companyId = await indexedDBStorage.getItem('companyId') || '';
        await followAPI.deleteAttachment({
          attachfileno: targetFile.attachfileno,
          companyid: companyId,
        });
      } catch {
        message.error('ファイルの削除に失敗しました');
        setDeleteConfirmVisible(false);
        setDeleteTargetIndex(null);
        return;
      }
    }
    const newFiles = files.filter((_, i) => i !== deleteTargetIndex);
    onFilesChange(newFiles);
    onFileDeleted(targetFile.attachfileno);
    setDeleteConfirmVisible(false);
    setDeleteTargetIndex(null);
  };

  const listContent = (
    <>
      {files.map((file, index) => (
        <div
          key={file.attachfileno ?? index}
          style={{
            padding: '8px',
            marginBottom: index < files.length - 1 ? '8px' : 0,
            borderBottom: index < files.length - 1 ? '1px solid #f0f0f0' : 'none'
          }}
        >
          {/* 1段目: クリップアイコン + ファイル名 + 容量 */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <PaperClipOutlined style={{ marginRight: '8px' }} />
            <span>{file.name}</span>
            <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>
              {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
            </span>
          </div>
          {/* 2段目: プレビュー・ダウンロード・削除ボタン */}
          <div style={{ display: 'flex', marginTop: '6px', gap: '4px' }}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              style={{ flex: 1 }}
              onClick={() => onFilePreview(file)}
            />
            <Button
              type="text"
              icon={<DownloadOutlined style={{ color: '#1677ff' }} />}
              style={{ flex: 1, backgroundColor: '#e6f4ff' }}
              onClick={() => onFileDownload(file)}
            />
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
              style={{ flex: 1, backgroundColor: '#fff0f0' }}
              onClick={() => {
                setDeleteTargetIndex(index);
                setDeleteConfirmVisible(true);
              }}
            />
          </div>
        </div>
      ))}
    </>
  );

  return (
    <>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: (zIndex ?? 1000) - 1 }} />
      )}
      <Modal
        title="添付ファイル"
        open={open}
        footer={<Button onClick={onClose}>閉じる</Button>}
        onCancel={onClose}
        centered
        mask={false}
        zIndex={zIndex}
      >
        {scrollable ? (
          <div style={{ maxHeight: '60dvh', overflowY: 'auto' }}>
            {listContent}
          </div>
        ) : listContent}
      </Modal>

      {deleteConfirmVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: (confirmZIndex ?? 1001) - 1 }} />
      )}
      <Modal
        title="ファイルの削除"
        open={deleteConfirmVisible}
        okText="削除"
        okType="danger"
        cancelText="キャンセル"
        zIndex={confirmZIndex}
        mask={false}
        centered
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setDeleteTargetIndex(null);
        }}
      >
        <p>「{targetFile?.name ?? ''}」を削除しますか？</p>
      </Modal>
    </>
  );
};

export default FollowAttachmentModal;
